import { useEffect, useRef, useState } from "react";
import { formatMoney } from "@/lib/currency";

/**
 * NumberRollup — animates a numeric value from 0 → target on mount.
 *
 * Uses a cubic ease-out curve and requestAnimationFrame for smooth 60fps
 * animation. Respects `prefers-reduced-motion` by rendering the final
 * value instantly. Formats via the shared `formatMoney` helper when
 * `currency` is provided, or `Intl.NumberFormat` for plain numbers.
 *
 * Props:
 *   value    — target number (required)
 *   currency — optional currency code (e.g. "USD"); when set, output
 *              is formatted with the correct symbol
 *   duration — ms, default 900
 *   suffix   — string appended after the number (e.g. "%", "mo")
 *   decimals — for plain-number mode
 *   className — pass-through
 */
export default function NumberRollup({
  value,
  currency,
  duration = 900,
  suffix = "",
  decimals = 0,
  className = "",
  ...rest
}) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  const startedRef = useRef(false);
  const landRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion — render final value instantly
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setDisplay(target); return; }

    // Only animate on the very first render for this component instance
    if (startedRef.current) { setDisplay(target); return; }
    startedRef.current = true;

    let rafId = 0;
    const startTs = performance.now();
    const startVal = 0;
    const delta = target - startVal;
    // easeOutCubic: 1 - (1-t)^3
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const elapsed = now - startTs;
      const t = Math.min(1, elapsed / duration);
      const eased = ease(t);
      setDisplay(startVal + delta * eased);
      if (t < 1) rafId = requestAnimationFrame(step);
      else if (landRef.current) {
        // Trigger the "land" pulse when the number reaches target
        landRef.current.classList.remove("animate-number-land");
        // force reflow so animation can re-trigger reliably
        void landRef.current.offsetWidth;
        landRef.current.classList.add("animate-number-land");
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
    // Intentionally only run on mount with initial value — avoids fighting parent re-renders
  }, [target, duration]);

  let text;
  if (currency) {
    text = formatMoney(display, currency);
  } else {
    text = display.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }) + suffix;
  }

  return (
    <span ref={landRef} className={`inline-block ${className}`} {...rest}>
      {text}
    </span>
  );
}
